import React, { useEffect, useState } from "react";
import {
  Container,
  Paper,
  Typography,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Button,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
} from "@mui/material";
import { ArrowBack, Edit } from "@mui/icons-material";
import DashboardLayout from "../../layouts/DashboardLayout";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getSystemHealthOverview } from "../../redux/actions";
import dayjs from "dayjs";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

export default function SystemUptimeLogs() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [sortDirection, setSortDirection] = useState("asc");

  const [durationFilter, setDurationFilter] = useState("");
  const [editingRow, setEditingRow] = useState(null);
  const [notes, setNotes] = useState({});

  useEffect(() => {
    dispatch(getSystemHealthOverview());

    const savedNotes = localStorage.getItem("uptimeNotes");
    if (savedNotes) {
      setNotes(JSON.parse(savedNotes));
    }
  }, [dispatch]);

  const systemuptimeList =
    useSelector(
      (state) =>
        state.dashboard.getSystemHealthDashboard.data?.details?.system_uptime
          ?.data
    ) || [];
  const now = dayjs();
  const rounded = now.startOf("hour");
  const filteredData = systemuptimeList.filter((row) => {
    if (!row?.Time) return false;

    let rowTime = dayjs(row.Time, [
      "DD/MM/YYYY, hh:mm A",
      "MM/DD/YYYY, hh:mm A",
      "YYYY-MM-DD hh:mm A",
    ]);

    if (!rowTime.isValid()) {
      rowTime = dayjs(
        `${dayjs().format("YYYY-MM-DD")} ${row.Time}`,
        "YYYY-MM-DD hh:mm A"
      );
    }

    if (!rowTime.isValid()) return false;

    const from =
      durationFilter === "Last 1 Hour"
        ? rounded.subtract(1, "hour")
        : durationFilter === "Last 6 Hours"
        ? rounded.subtract(6, "hour")
        : durationFilter === "Last 12 Hours"
        ? rounded.subtract(12, "hour")
        : null;

    if (!from) return true;

    const to = rounded;

    return (
      rowTime.valueOf() >= from.valueOf() &&
      rowTime.valueOf() < to.add(1, "hour").valueOf()
    );
  });

  filteredData.sort((a, b) => {
    const tA = dayjs(a.Time, ["DD/MM/YYYY, hh:mm A", "MM/DD/YYYY, hh:mm A"]);
    const tB = dayjs(b.Time, ["DD/MM/YYYY, hh:mm A", "MM/DD/YYYY, hh:mm A"]);
    return sortDirection === "asc"
      ? tA.isBefore(tB)
        ? -1
        : 1
      : tA.isAfter(tB)
      ? -1
      : 1;
  });

  const handleSaveNote = (idx) => {
    setEditingRow(null);

    localStorage.setItem("uptimeNotes", JSON.stringify(notes));
  };

  return (
    <DashboardLayout>
      <Container maxWidth={false} sx={{ py: 4, px: 5 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={3}
        >
          <Box>
            <FormControl
              size="small"
              variant="outlined"
              sx={{
                mb: 4,
                width: "220px",
                backgroundColor: "#fff",
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#BDBDBD",
                  },
                  "&:hover fieldset": {
                    borderColor: "#7B2D43",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#BDBDBD",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "#757575",
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#757575",
                },
              }}
            >
              <InputLabel id="duration-label">Duration</InputLabel>
              <Select
                labelId="duration-label"
                label="Duration"
                value={durationFilter}
                onChange={(e) => setDurationFilter(e.target.value)}
              >
                <MenuItem value="Last 1 Hour">Last 1 Hour</MenuItem>
                <MenuItem value="Last 6 Hours">Last 6 Hours</MenuItem>
                <MenuItem value="Last 12 Hours">Last 12 Hours</MenuItem>
                <MenuItem value="Last 24 Hours">Last 24 Hours</MenuItem>
              </Select>
            </FormControl>

            <Typography
              variant="h6"
              fontWeight={600}
              mb={1}
              sx={{ textAlign: "left" }}
            >
              System Up & Downtime Logs
            </Typography>
          </Box>

          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              px: 4,
              borderColor: "#7B2D43",
              color: "#7B2D43",
              fontWeight: 500,
              "&:hover": {
                borderColor: "#7B2D43",
                backgroundColor: "transparent",
              },
            }}
            startIcon={<ArrowBack />}
          >
            Back
          </Button>
        </Box>

        <Box display="flex" justifyContent="center">
          <Paper
            elevation={0}
            sx={{
              borderRadius: "12px",
              overflow: "hidden",
              border: "1px solid #ddd",
              width: "100%",
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#D0C5CA" }}>
                  <TableCell sx={{ fontWeight: 600, px: 3, py: 2 }}>
                    <Box display="flex" alignItems="center">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Date & Time
                      </Typography>
                      <Box display="flex" flexDirection="column" sx={{ ml: 1 }}>
                        <Box
                          onClick={() => setSortDirection("asc")}
                          sx={{
                            background: "rgb(225, 225, 225)",
                            border: "1px solid #ccc",
                            borderRadius: "2px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "18px",
                            height: "18px",
                            cursor: "pointer",
                            mb: "2px",
                          }}
                        >
                          <ArrowDropUpIcon
                            sx={{
                              fontSize: 16,
                              color:
                                sortDirection === "asc" ? "#7B2D43" : "#999",
                            }}
                          />
                        </Box>
                        <Box
                          onClick={() => setSortDirection("desc")}
                          sx={{
                            background: "rgb(225, 225, 225)",
                            border: "1px solid #ccc",
                            borderRadius: "2px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "18px",
                            height: "18px",
                            cursor: "pointer",
                          }}
                        >
                          <ArrowDropDownIcon
                            sx={{
                              fontSize: 16,
                              color:
                                sortDirection === "desc" ? "#7B2D43" : "#999",
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </TableCell>

                  <TableCell sx={{ fontWeight: 600, px: 3, py: 2 }}>
                    Uptime (%)
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, px: 3, py: 2 }}>
                    Downtime Duration
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, px: 3, py: 2 }}>
                    Downtime Reason
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, px: 3, py: 2 }}>
                    Notes
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.length > 0 ? (
                  filteredData.map((row, idx) => {
                    const rowKey = row["Time"];

                    return (
                      <TableRow
                        key={rowKey}
                        sx={{
                          "& td": { py: 2.5, borderBottom: "1px solid #ddd" },
                        }}
                      >
                        <TableCell>{row["Time"]}</TableCell>
                        <TableCell>{row["Uptime (%)"]}</TableCell>
                        <TableCell>{row["Downtime Duration"]}</TableCell>
                        <TableCell>{row["Downtime Reason"]}</TableCell>
                        <TableCell>
                          {editingRow === rowKey ? (
                            <TextField
                              size="small"
                              value={notes[rowKey] || ""}
                              onChange={(e) =>
                                setNotes({ ...notes, [rowKey]: e.target.value })
                              }
                              onBlur={() => handleSaveNote(rowKey)}
                              autoFocus
                              variant="outlined"
                            />
                          ) : (
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <span>{notes[rowKey] || "—"}</span>
                              <IconButton
                                size="small"
                                onClick={() => setEditingRow(rowKey)}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No data found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      </Container>
    </DashboardLayout>
  );
}
